import React from "react";
import { useTranslation } from "react-i18next";

type LoadingSkeletonProps = {
  width?: string;          // default: w-full
  height?: string;         // default: "" (line defaults to h-2)
  lines?: number;          // default: 3
  title?: boolean;         // default: false

  // optional styling hooks
  containerClassName?: string;
  lineClassName?: string;
};

export default function LoadingSkeleton({
  width = "w-full",
  height = "",
  lines = 3,
  title = false,
  containerClassName = "",
  lineClassName = "",
}: LoadingSkeletonProps) {
  const { t } = useTranslation("common");
  const lineHeight = height?.trim() ? height : "h-2";
  const bg = "bg-gray-200";
  const rounded = "rounded-full";

  // Slightly “natural” varying widths for readability
  const getLineWidth = (i: number) => {
    const variants = ["w-full", "w-5/6", "w-4/6", "w-4/5", "w-3/4"];
    return variants[i % variants.length];
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`space-y-3 animate-pulse ${containerClassName} ${width}`}
    >
      {title && (
        <div className={`${lineHeight} ${bg} ${rounded}`} />
      )}

      <div className="space-y-2">
        {Array.from({ length: Math.max(0, lines) }).map((_, i) => (
          <div
            key={i}
            className={`${lineHeight} ${bg} ${rounded} ${getLineWidth(i)} ${lineClassName}`}
          />
        ))}
      </div>

      <span className="sr-only">{t("Loading...")}</span>
    </div>
  );
}
